import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useThemeStore } from '@/stores/themeStore';
import {
  buildIframeSrcDoc,
  emailHasOwnCanvas,
  shouldClampEmailBody,
  EMAIL_CLAMP_MAX_HEIGHT,
} from './email-render.utils';

interface EmailHtmlFrameProps {
  /** Raw email HTML (may be a full `<!DOCTYPE html>` document or a fragment). */
  html: string;
  /** Optional aria/title for the frame. */
  title?: string;
  /**
   * When true (default), a body taller than `EMAIL_CLAMP_MAX_HEIGHT` is clamped
   * to that height with a bottom fade and a "Show more"/"Show less" toggle.
   * Short bodies render fully with no toggle.
   */
  collapsible?: boolean;
}

/**
 * Render an email HTML body inside a SANDBOXED iframe (`srcdoc`, NO scripts).
 *
 * Why an iframe and not a sanitized + scoped <div>:
 *   Email bodies are routinely full HTML documents carrying their own
 *   `<style>`, body backgrounds, and full-width layout tables. Rendered inline
 *   (even sanitized) the email's CSS bleeds into the app — full-width tables
 *   blow out the panel, dark backgrounds invert our theme. The iframe gives the
 *   email its own document + CSS scope so NOTHING leaks into the app layout.
 *
 * Security: the `sandbox` token list deliberately OMITS `allow-scripts`, so no
 * JavaScript in the email body can ever run (the headline threat for untrusted
 * HTML). We DO include `allow-same-origin` — without it the framed srcdoc gets
 * a unique opaque origin and the parent can't read `contentDocument` to measure
 * height. `allow-same-origin` WITHOUT `allow-scripts` is the safe combination:
 * the parent can read the DOM, but the email still has no script capability,
 * can't submit forms, and can't navigate the top frame. Links are forced to a
 * new tab in `measure()`.
 *
 * Auto-height: after load we read `contentDocument.body.scrollHeight` and size
 * the frame to its content (no inner scrollbar), capped to guard against a
 * pathological email. A ResizeObserver keeps it correct as images load.
 */
export function EmailHtmlFrame({
  html,
  title = 'Email content',
  collapsible = true,
}: EmailHtmlFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>(120);
  // Length-clamp state: collapsed by default once the body exceeds the
  // threshold; short bodies never get a toggle.
  const [expanded, setExpanded] = useState(false);
  // Theme drives the iframe's injected base colors so a fragment email reads
  // correctly in dark mode. A self-styled email (branded template / full doc)
  // opts out: it renders on a light canvas in BOTH themes, colors untouched.
  const isDark = useThemeStore((s) => s.resolvedTheme === 'dark');
  const ownCanvas = emailHasOwnCanvas(html);

  const srcDoc = buildIframeSrcDoc(html, { dark: isDark });

  // The frame's own surface must match the canvas the srcdoc paints, or a light
  // branded email shows a dark seam around/below its own background.
  const surfaceClass = ownCanvas ? 'bg-white' : 'bg-white dark:bg-gray-800';
  const fadeClass = ownCanvas ? 'from-white' : 'from-white dark:from-gray-800';

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let observer: ResizeObserver | null = null;

    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        // Force links to open in a new tab (the frame can't navigate top).
        doc.querySelectorAll('a[href]').forEach((a) => {
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener noreferrer');
        });
        const next = Math.min(
          Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight),
          4000, // hard cap so a pathological email can't grow without bound
        );
        setHeight(next + 4);
      } catch {
        /* cross-origin read should never happen for srcdoc; ignore defensively */
      }
    };

    const onLoad = () => {
      measure();
      try {
        const doc = iframe.contentDocument;
        if (doc?.body && typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(() => measure());
          observer.observe(doc.body);
        }
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener('load', onLoad);
    // srcdoc may already be loaded by the time the effect runs.
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      observer?.disconnect();
    };
  }, [srcDoc]);

  // Clamp only when enabled AND the measured body is meaningfully over the
  // threshold. The clamp lives on the OUTER wrapper (not inside the iframe), so
  // collapsed = threshold height with a bottom fade, expanded = full height.
  const canClamp = collapsible && shouldClampEmailBody(height);
  const clamped = canClamp && !expanded;

  const frame = (
    <iframe
      ref={iframeRef}
      title={title}
      // NO allow-scripts → email JS can never run (forms/top-nav also blocked).
      // allow-same-origin is required so the parent can read contentDocument to
      // auto-size the frame; it is safe ONLY because allow-scripts is absent.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      referrerPolicy="no-referrer"
      loading="lazy"
      // width:100% of the (full-width) email card — iframes otherwise default
      // to their intrinsic ~300px width, which caused the narrow-column regression.
      className={`w-full block border-0 ${surfaceClass}`}
      style={{ height }}
    />
  );

  if (!canClamp) return frame;

  return (
    <div className="w-full">
      <div
        className="relative w-full overflow-hidden transition-[max-height] duration-200"
        style={{ maxHeight: clamped ? EMAIL_CLAMP_MAX_HEIGHT : height }}
      >
        {frame}
        {clamped && (
          // Subtle bottom fade hinting there is more content below.
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t ${fadeClass} to-transparent`}
          />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}

export default EmailHtmlFrame;
